import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Tag, Space, Button, Avatar, Spin, Empty } from 'antd';
import {
  UserOutlined, StarOutlined, TrophyOutlined, EnvironmentOutlined,
  ShareAltOutlined, ArrowRightOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';
import FollowButton from '../../components/community/FollowButton';
import AdBanner from '../../components/AdBanner';

const { Title, Text, Paragraph } = Typography;

const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';
const BRAND_DARKER = '#075E54';
const INK = '#0b1f1a';

const SKILL_LABEL = {
  iniciante: 'Iniciante', recreativo: 'Recreativo',
  intermediario: 'Intermediário', avancado: 'Avançado', semi_pro: 'Semi-pro',
};

const TAG_META = {
  faz_gol:      { label: 'Faz gol',        emoji: '⚽' },
  bom_passe:    { label: 'Bom passe',      emoji: '🎯' },
  segura_setor: { label: 'Segura o setor', emoji: '🛡️' },
  muralha:      { label: 'Muralha',        emoji: '🧤' },
  corre_muito:  { label: 'Corre muito',    emoji: '🏃' },
  solidario:    { label: 'Solidário',      emoji: '🤝' },
  chega_hora:   { label: 'Chega na hora',  emoji: '⏰' },
  rachao:       { label: 'Rachão',         emoji: '🔥' },
};

// Slugs reservados — nunca devem ser tratados como perfil publico.
// Cobre paths que existem no app (admin e time-owner) pra evitar 404
// falso quando o browser cai aqui por engano (ex: organizador foi pra
// /team-config no hostname da comunidade).
const RESERVED_SLUGS = new Set([
  'team-config', 'dashboard', 'organizer', 'admin', 'login', 'logout',
  'signup', 'cadastro', 'ativacao', 'auth-tk', 'forgot-password',
  'reset-password', 'avaliar', 'reativar', 'time', 'player', 'jogador',
  'community', 'customers', 'jogadores', 'jogos', 'ranking', 'frequencia',
  'cobrancas', 'administradores', 'minha-assinatura', 'minha-conta',
  'indicacoes', 'ajuda', 'configuracao', 'termos', 'como-funciona-ranking',
  'como-avaliar',
]);

// Perfil publico compartilhavel: comunidade.futezap.com.br/jogador/{slug}
// (URL antiga /{slug} continua funcionando via redirect legacyRedirect prop).
// Sem auth. Pensado pra abrir bem no whatsapp/insta (open graph).
export default function PublicPlayerProfile({ legacyRedirect = false }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [privateProfile, setPrivateProfile] = useState(null); // {name, profile_photo} se dono desmarcou is_public

  useEffect(() => {
    if (RESERVED_SLUGS.has(String(slug).toLowerCase())) {
      window.location.href = '/';
      return;
    }
    // Backwards-compat: URL antiga /{slug} redireciona pra nova /jogador/{slug}
    // preservando link compartilhado antigo. replace pra nao poluir historico.
    if (legacyRedirect) {
      window.location.replace(`/jogador/${slug}`);
      return;
    }
    api.get(`/community/players/by-slug/${slug}`)
      .then(r => {
        if (r.data?.private) setPrivateProfile(r.data.player || null);
        else setPlayer(r.data);
      })
      .catch(err => { if (err?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
    // Atualiza title da aba
    document.title = `${slug} · Futezap Comunidade`;
    return () => { document.title = 'Futezap'; };
  }, [slug]);

  useEffect(() => {
    // Meta tags dinamicas pra Open Graph (share bonito no zap/insta)
    if (!player) return;
    const setMeta = (prop, content) => {
      let m = document.querySelector(`meta[property="${prop}"]`);
      if (!m) { m = document.createElement('meta'); m.setAttribute('property', prop); document.head.appendChild(m); }
      m.setAttribute('content', content);
    };
    setMeta('og:title', `${player.name} · Futezap Comunidade`);
    setMeta('og:description', `Boleiro ${SKILL_LABEL[player.skill_level] || ''} · ${player.city || ''} · ⭐ ${Number(player.average_rating || 0).toFixed(1)}`);
    if (player.profile_photo) setMeta('og:image', absoluteUploadUrl(player.profile_photo));
    setMeta('og:url', window.location.href);
    setMeta('og:type', 'profile');
  }, [player]);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  if (privateProfile) {
    const initial = (privateProfile.name || '?').trim()[0]?.toUpperCase() || '?';
    return (
      <div style={{ minHeight: '100vh', background: '#f5f7fa', padding: '80px 20px', textAlign: 'center' }}>
        <div style={{
          maxWidth: 400, margin: '0 auto', background: '#fff',
          borderRadius: 20, padding: 40, boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
        }}>
          {privateProfile.profile_photo ? (
            <img src={absoluteUploadUrl(privateProfile.profile_photo)}
              alt="" width={96} height={96}
              style={{ borderRadius: 48, objectFit: 'cover', filter: 'blur(10px) grayscale(0.4)' }} />
          ) : (
            <div style={{
              width: 96, height: 96, borderRadius: 48, background: '#128C7E',
              color: '#fff', fontSize: 42, fontWeight: 800, lineHeight: '96px', margin: '0 auto',
              filter: 'blur(6px) opacity(0.6)',
            }}>{initial}</div>
          )}
          <div style={{ fontSize: 24, fontWeight: 800, marginTop: 20, color: '#0b1f1a' }}>
            🔒 Perfil privado
          </div>
          <div style={{ fontSize: 14, color: '#5a6a63', marginTop: 12, lineHeight: 1.55 }}>
            {privateProfile.name ? <><b>{privateProfile.name}</b> optou por</> : 'Esse jogador optou por'} manter o perfil escondido.
            Só quem o jogador convida pode ver os detalhes.
          </div>
          <Button type="primary" style={{ marginTop: 24, borderRadius: 12, height: 44, paddingInline: 24 }}
            onClick={() => nav('/')}>
            Ir pra Comunidade
          </Button>
        </div>
      </div>
    );
  }

  if (notFound || !player) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f7fa', padding: '60px 20px', textAlign: 'center' }}>
        <Empty description="Esse perfil não existe ou foi removido" />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => nav('/')}>Ir pra Comunidade</Button>
      </div>
    );
  }

  const share = async () => {
    const url = window.location.href;
    const text = `Confere meu perfil no Futezap: ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: player.name, text, url }); } catch { /* ignore */ }
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copiado!');
    }
  };

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <AdBanner publicMode screenOverride="/perfil-publico-jogador" />
      {/* Header verde grande com foto */}
      <div style={{
        background: `radial-gradient(ellipse at top left, ${BRAND_DARK}, transparent 50%),
                     radial-gradient(ellipse at top right, ${BRAND}, transparent 50%),
                     linear-gradient(180deg, ${BRAND_DARKER} 0%, #041a15 100%)`,
        color: '#fff', padding: '32px 20px 60px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320,
                      borderRadius: '50%', background: BRAND, opacity: 0.25, filter: 'blur(80px)' }} />
        <div style={{ position: 'relative', maxWidth: 500, margin: '0 auto' }}>
          {/* Logo Futezap no topo */}
          <a href="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            marginBottom: 24, textDecoration: 'none', color: '#fff',
          }}>
            <img src="/pwa/icon-192.png" alt="Futezap" width={36} height={36}
              style={{ borderRadius: 10, boxShadow: '0 6px 14px rgba(0,0,0,0.35)' }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3 }}>Futezap</div>
              <div style={{ fontSize: 9, opacity: 0.7, letterSpacing: 1.5, fontWeight: 600 }}>COMUNIDADE</div>
            </div>
          </a>

          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Avatar size={88} src={absoluteUploadUrl(player.profile_photo)} icon={<UserOutlined />}
              style={{ border: '3px solid #fff', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: -0.5 }}>{player.name}</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                <EnvironmentOutlined /> {[player.neighborhood, player.city, player.state].filter(Boolean).join(' · ') || 'Sem localização'}
              </div>
              <Space size={4} wrap style={{ marginTop: 6 }}>
                {player.preferred_position && <Tag color="blue" style={{ margin: 0 }}>{player.preferred_position}</Tag>}
                {player.skill_level && <Tag color="green" style={{ margin: 0 }}>{SKILL_LABEL[player.skill_level] || player.skill_level}</Tag>}
                {player.level && (
                  <Tag style={{
                    margin: 0,
                    background: player.level.color || '#faad14',
                    color: '#fff', border: 'none', fontWeight: 700,
                  }}>
                    <TrophyOutlined /> {player.level.label}
                  </Tag>
                )}
                {player.streak_current > 0 && (
                  <Tag style={{
                    margin: 0, background: 'rgba(255,92,51,0.85)', color: '#fff',
                    border: 'none', fontWeight: 700,
                  }}>
                    🔥 {player.streak_current} {player.streak_current === 1 ? 'dia' : 'dias'}
                  </Tag>
                )}
              </Space>
            </div>
          </div>

          {/* Stats grandes (4 se tem XP, senao 3) */}
          <div style={{
            marginTop: 24, display: 'grid',
            gridTemplateColumns: `repeat(${player.total_xp > 0 ? 4 : 3}, 1fr)`, gap: 8,
          }}>
            {player.total_xp > 0 && (
              <div style={{
                background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(6px)', borderRadius: 12,
                padding: '10px 6px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.15)',
              }}>
                <div style={{ fontSize: 22, fontWeight: 900, background: `linear-gradient(90deg, ${BRAND}, #a3e635)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {(player.total_xp || 0).toLocaleString('pt-BR')}
                </div>
                <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2, letterSpacing: 0.5 }}>XP</div>
              </div>
            )}
            <div style={{
              background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(6px)', borderRadius: 12,
              padding: '10px 6px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.15)',
            }}>
              <div style={{ fontSize: 22, fontWeight: 900, background: `linear-gradient(90deg, ${BRAND}, #a3e635)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {Number(player.average_rating || 0).toFixed(1)}
              </div>
              <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2, letterSpacing: 0.5 }}>RATING</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(6px)', borderRadius: 12,
              padding: '10px 6px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.15)',
            }}>
              <div style={{ fontSize: 22, fontWeight: 900 }}>{player.total_games || 0}</div>
              <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2, letterSpacing: 0.5 }}>JOGOS</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(6px)', borderRadius: 12,
              padding: '10px 6px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.15)',
            }}>
              <div style={{ fontSize: 22, fontWeight: 900 }}>{player.total_ratings || 0}</div>
              <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2, letterSpacing: 0.5 }}>AVAL.</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <FollowButton targetType="player" targetId={player.id} size="large"
              style={{ flex: 1, height: 44 }} />
            <Button size="large" icon={<ShareAltOutlined />} onClick={share}
              style={{
                height: 44, borderRadius: 10, fontWeight: 700, padding: '0 16px',
                background: 'rgba(255,255,255,0.15)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(6px)',
              }}>
              Compartilhar
            </Button>
          </div>
        </div>
      </div>

      {/* Bio + info extras */}
      <div style={{ maxWidth: 500, margin: '-30px auto 0', padding: '0 20px 40px', position: 'relative' }}>
        {player.bio && (
          <Card style={{ borderRadius: 16, marginBottom: 12, borderLeft: `4px solid ${BRAND}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: BRAND_DARK, letterSpacing: 0.5, marginBottom: 6 }}>
              BIO
            </div>
            <div style={{ fontSize: 15, color: INK, lineHeight: 1.55, fontStyle: 'italic' }}>
              "{player.bio}"
            </div>
          </Card>
        )}

        {Array.isArray(player.teams) && player.teams.length > 0 && (
          <Card style={{ borderRadius: 16, marginBottom: 12 }}
            title={<><TrophyOutlined /> Times onde joga</>}>
            <div style={{ display: 'grid', gap: 10 }}>
              {player.teams.map(t => {
                const href = t.slug ? `/time/${t.slug}` : null;
                const inner = (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: 10, borderRadius: 12, background: '#f9fafb',
                    border: '1px solid #eee',
                    cursor: href ? 'pointer' : 'default',
                  }}>
                    <Avatar size={44} src={absoluteUploadUrl(t.logo_url)} icon={<TrophyOutlined />}
                      style={{ background: BRAND_DARK, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: INK }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        {[t.city, t.state].filter(Boolean).join(' / ')}
                        {t.games_played > 0 && ` · ${t.games_played} jogo${t.games_played > 1 ? 's' : ''}`}
                      </div>
                    </div>
                    {t.type === 'futezap' && <Tag color="gold" style={{ margin: 0 }}>Pro</Tag>}
                  </div>
                );
                return href ? (
                  <a key={t.id} href={href} style={{ textDecoration: 'none', color: 'inherit' }}>{inner}</a>
                ) : <div key={t.id}>{inner}</div>;
              })}
            </div>
          </Card>
        )}

        {Array.isArray(player.endorsements) && player.endorsements.length > 0 && (
          <Card style={{ borderRadius: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: BRAND_DARK, letterSpacing: 0.5, marginBottom: 10 }}>
              ELOGIOS DA GALERA
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {player.endorsements.map(e => {
                const meta = TAG_META[e.tag] || { label: e.tag, emoji: '⭐' };
                return (
                  <div key={e.tag} style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: '#f0fdf4', border: `1px solid ${BRAND}55`,
                    fontSize: 13, fontWeight: 600, color: INK,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span>{meta.emoji}</span>
                    <span>{meta.label}</span>
                    <span style={{ color: BRAND_DARK, fontWeight: 800 }}>· {e.n || e.count}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {player.game_price && Number(player.game_price) > 0 && (
          <Card style={{ borderRadius: 16, marginBottom: 12, background: '#fffbe6', border: '1px solid #ffe58f' }}>
            <div style={{ fontSize: 12, color: '#8a6d3b', fontWeight: 700, letterSpacing: 0.5 }}>VALOR PRA JOGAR</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#d48806', marginTop: 4 }}>
              R$ {Number(player.game_price).toFixed(2)}
            </div>
            <div style={{ fontSize: 12, color: '#5a6a63', marginTop: 4 }}>
              Valor combinado por jogo (comum pra goleiro)
            </div>
          </Card>
        )}

        {/* CTA de conversão — SO pra visitante nao-logado (jogador ou
            organizador ja logado nao precisa ver). */}
        {!localStorage.getItem('community_token') && !localStorage.getItem('token') && (
          <Card style={{
            borderRadius: 16, marginTop: 16,
            background: `linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)`,
            border: `2px solid ${BRAND}`,
          }}>
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <ThunderboltOutlined style={{ fontSize: 26, color: BRAND }} />
              <Title level={4} style={{ margin: '8px 0 4px', color: INK }}>Também joga bola?</Title>
              <Paragraph style={{ color: '#5a6a63', marginBottom: 16 }}>
                Cria teu perfil no Futezap Comunidade. Grátis, sem cartão.
              </Paragraph>
              <Button type="primary" size="large" block
                onClick={() => nav('/player/login')}
                style={{
                  height: 48, borderRadius: 12, background: BRAND, borderColor: BRAND,
                  fontWeight: 800, boxShadow: '0 8px 20px rgba(37, 211, 102, 0.35)',
                }}>
                Entrar / Criar meu perfil <ArrowRightOutlined />
              </Button>
            </div>
          </Card>
        )}

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: '#8c8c8c' }}>
          Feito no Brasil 🇧🇷 pra quem joga bola
        </div>
      </div>
    </div>
  );
}
